import { TmsStatePanel } from "@/components/tms/TmsStatePanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { useFleetMode } from "@/lib/tms/fleet-mode-context";
import {
  FLEET_MODE_LABELS,
  getFleetModeCapabilityMessage,
} from "@/lib/tms/fleet-mode";
import { Skeleton } from "@/components/ui/skeleton";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";

type LoadDetail = {
  id: string;
  reference_number: string;
  status: string;
  coverage_type: string | null;
  origin_name: string | null;
  origin_address: string;
  origin_city: string;
  origin_state: string;
  origin_zip: string;
  pickup_window_start: string | null;
  pickup_window_end: string | null;
  dest_name: string | null;
  dest_address: string;
  dest_city: string;
  dest_state: string;
  dest_zip: string;
  delivery_window_start: string | null;
  delivery_window_end: string | null;
  equipment_type: string;
  weight_lbs: number | null;
  commodity: string | null;
  special_instructions: string | null;
  customer_name: string | null;
  customer_ref: string | null;
  recommended_coverage: string | null;
  recommendation_reason: string | null;
  recommendation_options: unknown;
  current_assignment: {
    truck_unit: string | null;
    driver_name: string | null;
    trailer_unit: string | null;
  } | null;
  latest_tender: {
    id: string;
    status: string;
    carrier_name: string;
    offered_rate: number | null;
    sent_at: string;
  } | null;
  recent_events: TrackingEvent[];
  open_exceptions: Exception[];
};

type TrackingEvent = {
  id: string;
  event_type: string;
  source: string;
  message: string | null;
  lat: number | null;
  lon: number | null;
  location_label: string | null;
  status_value: string | null;
  created_at: string;
  created_by: string | null;
};

type Exception = {
  id: string;
  type: string;
  severity: string;
  status: string;
  created_at: string;
};

type AvailableFleet = {
  trucks: Array<{ id: string; unit_number: string; equipment_type: string }>;
  drivers: Array<{ id: string; first_name: string; last_name: string }>;
  trailers: Array<{ id: string; unit_number: string }>;
};

type Carrier = { id: string; name: string };

const EVENT_TYPE_LABEL: Record<string, string> = {
  location_update: "Location Update",
  status_change: "Status Change",
  note: "Note",
  samsara_ping: "GPS Ping",
  tender_sent: "Tender Sent",
  tender_accepted: "Tender Accepted",
  tender_rejected: "Tender Rejected",
  exception: "Exception",
  manual_update: "Manual Update",
  assignment_changed: "Assignment Changed",
  reassignment: "Reassignment",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Badge({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${className ?? ""}`}>
      {children}
    </span>
  );
}

export default function LoadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { fleetMode } = useFleetMode();
  const [load, setLoad] = React.useState<LoadDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState("overview");

  // Timeline
  const [timeline, setTimeline] = React.useState<TrackingEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = React.useState(false);
  const [noteText, setNoteText] = React.useState("");
  const [addingNote, setAddingNote] = React.useState(false);

  // Assign fleet
  const [fleet, setFleet] = React.useState<AvailableFleet | null>(null);
  const [assignForm, setAssignForm] = React.useState({ truck_id: "", driver_id: "", trailer_id: "" });
  const [assigning, setAssigning] = React.useState(false);
  const [assignmentError, setAssignmentError] = React.useState<string | null>(null);

  // Tender modal
  const [carriers, setCarriers] = React.useState<Carrier[]>([]);
  const [tenderForm, setTenderForm] = React.useState({ carrier_id: "", offered_rate: "", expires_at: "" });
  const [tendering, setTendering] = React.useState(false);
  const [showTenderModal, setShowTenderModal] = React.useState(false);
  const [tenderError, setTenderError] = React.useState<string | null>(null);

  // Recommend
  const [recommending, setRecommending] = React.useState(false);
  const canAssignFleet = fleetMode !== "third_party_only";
  const canTenderCarrier = fleetMode !== "own_fleet_only";

  async function loadDetail() {
    if (!id) return;
    try {
      const res = await fetch(`/api/tms/dispatch/loads/${id}`);
      const json = (await res.json()) as { status: string; data?: LoadDetail; error?: string };
      if (json.status === "success" && json.data) {
        setLoad(json.data);
      } else {
        setError(json.error ?? "Failed to load.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Network error.");
    } finally {
      setLoading(false);
    }
  }

  async function loadTimeline() {
    if (!id) return;
    setTimelineLoading(true);
    const res = await fetch(`/api/tms/dispatch/loads/${id}/timeline`);
    const json = (await res.json()) as { status: string; data?: TrackingEvent[] };
    if (json.status === "success" && json.data) setTimeline(json.data);
    setTimelineLoading(false);
  }

  async function loadFleet() {
    const res = await fetch("/api/tms/fleet/available");
    const json = (await res.json()) as { status: string; data?: AvailableFleet };
    if (json.status === "success" && json.data) setFleet(json.data);
  }

  async function loadCarriers() {
    const res = await fetch("/api/tms/dispatch/carriers");
    const json = (await res.json()) as { status: string; data?: Carrier[] };
    if (json.status === "success" && json.data) setCarriers(json.data);
  }

  React.useEffect(() => {
    void loadDetail();
  }, [id]);

  React.useEffect(() => {
    if (activeTab === "timeline") void loadTimeline();
    if (activeTab === "assignment") {
      if (canAssignFleet) void loadFleet();
      if (canTenderCarrier) void loadCarriers();
    }
    if (activeTab === "tender" && canTenderCarrier) void loadCarriers();
  }, [activeTab, canAssignFleet, canTenderCarrier]);

  React.useEffect(() => {
    if (!canTenderCarrier) {
      setShowTenderModal(false);
    }
  }, [canTenderCarrier]);

  async function handleAddNote(e: React.FormEvent) {
    e.preventDefault();
    if (!noteText.trim() || !id) return;
    setAddingNote(true);
    const res = await fetch(`/api/tms/dispatch/loads/${id}/timeline`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: noteText }),
    });
    const json = (await res.json()) as { status: string };
    if (json.status === "success") {
      setNoteText("");
      await loadTimeline();
    }
    setAddingNote(false);
  }

  async function handleAssignFleet(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setAssignmentError(null);
    setAssigning(true);
    const res = await fetch(`/api/tms/dispatch/loads/${id}/assign-fleet`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        truck_id: assignForm.truck_id || undefined,
        driver_id: assignForm.driver_id || undefined,
        trailer_id: assignForm.trailer_id || undefined,
      }),
    });
    const json = (await res.json()) as { status: string; error?: string };
    if (json.status === "success") {
      await loadDetail();
    } else {
      setAssignmentError(json.error ?? "Unable to assign fleet.");
    }
    setAssigning(false);
  }

  async function handleSendTender(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !tenderForm.carrier_id) return;
    setTenderError(null);
    setTendering(true);
    const res = await fetch(`/api/tms/dispatch/loads/${id}/tender`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        carrier_id: tenderForm.carrier_id,
        offered_rate: tenderForm.offered_rate ? Number(tenderForm.offered_rate) : undefined,
        expires_at: tenderForm.expires_at || undefined,
      }),
    });
    const json = (await res.json()) as { status: string; error?: string };
    if (json.status === "success") {
      setShowTenderModal(false);
      await loadDetail();
    } else {
      setTenderError(json.error ?? "Unable to send tender.");
    }
    setTendering(false);
  }

  async function handleRecommend() {
    if (!id) return;
    setRecommending(true);
    await fetch(`/api/tms/dispatch/loads/${id}/recommend`, { method: "POST" });
    await loadDetail();
    setRecommending(false);
  }

  const tabs = ["overview", "assignment", "timeline", "tender"];
  const analysisHint = fleetMode === "own_fleet_only"
    ? "Run Analysis to check whether your available fleet can cover this load."
    : fleetMode === "third_party_only"
      ? "Run Analysis to compare preferred and spot carrier options for this lane."
      : 'Click "Run Analysis" to get coverage recommendations.';

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !load) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dispatch")}>← Back to Dispatch</Button>
        <p className="text-destructive">{error ?? "Load not found."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dispatch")}>← Back</Button>
          <div>
            <h1 className="text-xl font-semibold">{load.reference_number}</h1>
            <p className="text-xs text-muted-foreground">{load.origin_city}, {load.origin_state} → {load.dest_city}, {load.dest_state}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {load.open_exceptions.length > 0 ? (
            <span className="rounded-full bg-red-100 text-red-700 text-xs font-bold px-2.5 py-0.5">
              {load.open_exceptions.length} exception{load.open_exceptions.length !== 1 ? "s" : ""}
            </span>
          ) : null}
          <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground capitalize">
            {load.status.replace("_", " ")}
          </span>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b pb-0">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
              activeTab === tab
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === "overview" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Origin</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                {load.origin_name ? <p className="font-medium">{load.origin_name}</p> : null}
                <p>{load.origin_address}</p>
                <p>{load.origin_city}, {load.origin_state} {load.origin_zip}</p>
                {load.pickup_window_start ? <p className="text-muted-foreground text-xs">Pickup: {formatDate(load.pickup_window_start)}</p> : null}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Destination</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                {load.dest_name ? <p className="font-medium">{load.dest_name}</p> : null}
                <p>{load.dest_address}</p>
                <p>{load.dest_city}, {load.dest_state} {load.dest_zip}</p>
                {load.delivery_window_start ? <p className="text-muted-foreground text-xs">Delivery: {formatDate(load.delivery_window_start)}</p> : null}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Equipment</p>
              <p className="font-medium capitalize">{load.equipment_type.replace("_", " ")}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Weight</p>
              <p className="font-medium">{load.weight_lbs ? `${load.weight_lbs.toLocaleString()} lbs` : "—"}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Commodity</p>
              <p className="font-medium">{load.commodity ?? "—"}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Customer</p>
              <p className="font-medium">{load.customer_name ?? "—"}</p>
            </div>
          </div>

          {/* Rules engine recommendation */}
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Coverage Recommendation</CardTitle>
              <Button size="sm" variant="outline" disabled={recommending} onClick={() => void handleRecommend()}>
                {recommending ? "Analyzing..." : "Run Analysis"}
              </Button>
            </CardHeader>
            <CardContent className="text-sm">
              <p className="mb-3 text-xs text-muted-foreground">Current mode: {FLEET_MODE_LABELS[fleetMode]}</p>
              {load.recommended_coverage ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                      {load.recommended_coverage.replace("_", " ")}
                    </Badge>
                    <span className="text-muted-foreground">{load.recommendation_reason}</span>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">{analysisHint}</p>
              )}
            </CardContent>
          </Card>

          {/* Exceptions */}
          {load.open_exceptions.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-red-600">Open Exceptions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {load.open_exceptions.map((ex) => (
                  <div key={ex.id} className="flex items-center justify-between text-sm rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/30 px-3 py-2">
                    <span className="font-medium capitalize">{ex.type.replace(/_/g, " ")}</span>
                    <Badge className={ex.severity === "critical" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}>
                      {ex.severity}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ASSIGNMENT TAB */}
      {activeTab === "assignment" && (
        <div className="space-y-4">
          {load.current_assignment && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Current Assignment</CardTitle></CardHeader>
              <CardContent className="text-sm grid grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Truck</p>
                  <p className="font-medium">{load.current_assignment.truck_unit ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Driver</p>
                  <p className="font-medium">{load.current_assignment.driver_name ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Trailer</p>
                  <p className="font-medium">{load.current_assignment.trailer_unit ?? "—"}</p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{load.current_assignment ? "Reassign Fleet" : "Assign Fleet"}</CardTitle>
            </CardHeader>
            <CardContent>
              {canAssignFleet ? (
                <form onSubmit={(e) => void handleAssignFleet(e)} className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Truck</label>
                      <select
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        value={assignForm.truck_id}
                        onChange={(e) => setAssignForm((f) => ({ ...f, truck_id: e.target.value }))}
                      >
                        <option value="">— Select Truck —</option>
                        {fleet?.trucks.map((t) => (
                          <option key={t.id} value={t.id}>{t.unit_number} ({t.equipment_type})</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Driver</label>
                      <select
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        value={assignForm.driver_id}
                        onChange={(e) => setAssignForm((f) => ({ ...f, driver_id: e.target.value }))}
                      >
                        <option value="">— Select Driver —</option>
                        {fleet?.drivers.map((d) => (
                          <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Trailer (optional)</label>
                      <select
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        value={assignForm.trailer_id}
                        onChange={(e) => setAssignForm((f) => ({ ...f, trailer_id: e.target.value }))}
                      >
                        <option value="">— No Trailer —</option>
                        {fleet?.trailers.map((t) => (
                          <option key={t.id} value={t.id}>{t.unit_number}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {assignmentError ? <p className="text-sm text-destructive">{assignmentError}</p> : null}
                  <Button type="submit" size="sm" disabled={assigning}>
                    {assigning ? "Assigning..." : load.current_assignment ? "Reassign" : "Assign Fleet"}
                  </Button>
                </form>
              ) : (
                <TmsStatePanel
                  tone="filtered"
                  title="Fleet assignments are unavailable in Carrier Network mode."
                  description={getFleetModeCapabilityMessage(fleetMode, "assign_fleet")}
                  actionLabel="Open TMS Settings"
                  onAction={() => navigate("/account/settings")}
                  className="py-8"
                />
              )}
            </CardContent>
          </Card>

          {/* Tender to carrier */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Tender to Carrier</CardTitle></CardHeader>
            <CardContent>
              {!canTenderCarrier ? (
                <TmsStatePanel
                  tone="filtered"
                  title="Carrier tendering is unavailable in Own Fleet TMS mode."
                  description={getFleetModeCapabilityMessage(fleetMode, "tender_carrier")}
                  actionLabel="Open TMS Settings"
                  onAction={() => navigate("/account/settings")}
                  className="py-8"
                />
              ) : showTenderModal ? (
                <form onSubmit={(e) => void handleSendTender(e)} className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Carrier *</label>
                      <select
                        required
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        value={tenderForm.carrier_id}
                        onChange={(e) => setTenderForm((f) => ({ ...f, carrier_id: e.target.value }))}
                      >
                        <option value="">— Select Carrier —</option>
                        {carriers.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Offered Rate ($)</label>
                      <input
                        type="number"
                        placeholder="2500"
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        value={tenderForm.offered_rate}
                        onChange={(e) => setTenderForm((f) => ({ ...f, offered_rate: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Expires At</label>
                      <DateTimePicker
                        value={tenderForm.expires_at}
                        onChange={(value) => setTenderForm((f) => ({ ...f, expires_at: value }))}
                      />
                    </div>
                  </div>
                  {tenderError ? <p className="text-sm text-destructive">{tenderError}</p> : null}
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={tendering}>{tendering ? "Sending..." : "Send Tender"}</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowTenderModal(false)}>Cancel</Button>
                  </div>
                </form>
              ) : (
                <Button size="sm" variant="outline" onClick={() => { void loadCarriers(); setShowTenderModal(true); }}>
                  Send to Carrier
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* TIMELINE TAB */}
      {activeTab === "timeline" && (
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <form onSubmit={(e) => void handleAddNote(e)} className="flex gap-2">
                <input
                  className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Add a note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
                <Button type="submit" size="sm" disabled={addingNote || !noteText.trim()}>
                  {addingNote ? "..." : "Add Note"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {timelineLoading ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : (
            <div className="space-y-2">
              {timeline.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No tracking events yet.</p>
              ) : timeline.map((ev) => (
                <div key={ev.id} className="flex gap-3 rounded-lg border p-3 text-sm">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{EVENT_TYPE_LABEL[ev.event_type] ?? ev.event_type}</span>
                      <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{ev.source}</span>
                    </div>
                    {ev.message ? <p className="text-muted-foreground mt-0.5">{ev.message}</p> : null}
                    {ev.lat && ev.lon ? <p className="text-xs text-muted-foreground">GPS: {ev.lat}, {ev.lon}</p> : null}
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(ev.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TENDER TAB */}
      {activeTab === "tender" && (
        <div className="space-y-4">
          {load.latest_tender ? (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Latest Tender</CardTitle></CardHeader>
              <CardContent className="text-sm grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div>
                  <p className="text-xs text-muted-foreground">Carrier</p>
                  <p className="font-medium">{load.latest_tender.carrier_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p className="font-medium capitalize">{load.latest_tender.status}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Rate</p>
                  <p className="font-medium">{load.latest_tender.offered_rate ? `$${load.latest_tender.offered_rate}` : "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Sent</p>
                  <p className="font-medium">{formatDate(load.latest_tender.sent_at)}</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <p className="text-sm text-muted-foreground">
              {canTenderCarrier
                ? "No tenders sent for this load yet. Go to the Assignment tab to tender to a carrier."
                : "No tenders have been sent for this load."}
            </p>
          )}

          {!canTenderCarrier ? (
            <TmsStatePanel
              tone="filtered"
              title="Carrier tendering is unavailable in Own Fleet TMS mode."
              description={getFleetModeCapabilityMessage(fleetMode, "tender_carrier")}
              actionLabel="Open TMS Settings"
              onAction={() => navigate("/account/settings")}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
